export const ADDITIONAL_FIELD_NAME_PATTERN = /^[A-Za-z0-9_]+$/;

export const isAdditionalFieldNameSyntaxValid = (fieldName) =>
  ADDITIONAL_FIELD_NAME_PATTERN.test(String(fieldName || ""));

export const getAdditionalFieldNameValidationError = (
  fields = [],
  reservedNames = [],
) => {
  const usedNames = new Set(reservedNames);

  for (let index = 0; index < fields.length; index += 1) {
    const field = fields[index] || {};

    if (!field.fieldType || field.fieldType === "Textblock") continue;

    const fieldName = String(field.fieldName || "");

    if (!fieldName) {
      return {
        index,
        message: `Additional field #${index + 1} requires a field name.`,
      };
    }

    if (!isAdditionalFieldNameSyntaxValid(fieldName)) {
      return {
        index,
        message: `Additional field #${index + 1} name can only contain letters, numbers, and underscores.`,
      };
    }

    if (usedNames.has(fieldName)) {
      return {
        index,
        message: `Additional field #${index + 1} name is already in use.`,
      };
    }

    usedNames.add(fieldName);
  }

  return null;
};

export const normalizeAdditionalFieldExpansion = (
  expansionState = [],
  fieldCount = 0,
  defaultExpanded = true,
) =>
  Array.from(
    { length: fieldCount },
    (_, index) => expansionState[index] ?? defaultExpanded,
  );

export const moveAdditionalField = (fields = [], fromIndex, toIndex) => {
  const movedFields = [...fields];

  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= movedFields.length ||
    toIndex >= movedFields.length
  ) {
    return movedFields;
  }

  const [movedField] = movedFields.splice(fromIndex, 1);
  movedFields.splice(toIndex, 0, movedField);
  return movedFields;
};

export const moveAdditionalFieldExpansion = (
  expansionState,
  fieldCount,
  fromIndex,
  toIndex,
  defaultExpanded = true,
) =>
  moveAdditionalField(
    normalizeAdditionalFieldExpansion(
      expansionState,
      fieldCount,
      defaultExpanded,
    ),
    fromIndex,
    toIndex,
  );

export const appendCollapsedAdditionalFieldExpansion = (
  expansionState,
  fieldCount,
  defaultExpanded = true,
) => [
  ...normalizeAdditionalFieldExpansion(
    expansionState,
    fieldCount,
    defaultExpanded,
  ),
  false,
];

export const insertCollapsedAdditionalFieldExpansion = (
  expansionState,
  fieldCount,
  insertedIndex,
) => {
  const nextExpansionState = normalizeAdditionalFieldExpansion(
    expansionState,
    fieldCount,
  );

  if (insertedIndex >= 0 && insertedIndex <= nextExpansionState.length) {
    nextExpansionState.splice(insertedIndex, 0, false);
  }

  return nextExpansionState;
};

const cloneAdditionalField = (field) =>
  JSON.parse(JSON.stringify(field || {}));

export const getDuplicateAdditionalFieldName = (
  fields = [],
  fieldName,
) => {
  if (!fieldName) return "";

  const usedNames = new Set(
    fields.map((field) => field?.fieldName).filter(Boolean),
  );
  const baseName = `${fieldName}_copy`;
  let candidateName = baseName;
  let copyNumber = 2;

  while (usedNames.has(candidateName)) {
    candidateName = `${baseName}_${copyNumber}`;
    copyNumber += 1;
  }

  return candidateName;
};

export const duplicateAdditionalField = (fields = [], sourceIndex) => {
  if (sourceIndex < 0 || sourceIndex >= fields.length) return [...fields];

  const copiedField = cloneAdditionalField(fields[sourceIndex]);
  if (copiedField.fieldName) {
    copiedField.fieldName = getDuplicateAdditionalFieldName(
      fields,
      copiedField.fieldName,
    );
  }

  const duplicatedFields = [...fields];
  duplicatedFields.splice(sourceIndex + 1, 0, copiedField);
  return duplicatedFields;
};

export const removeAdditionalFieldExpansion = (
  expansionState,
  fieldCount,
  removedIndex,
  defaultExpanded = true,
) => {
  const nextExpansionState = normalizeAdditionalFieldExpansion(
    expansionState,
    fieldCount,
    defaultExpanded,
  );

  if (removedIndex >= 0 && removedIndex < nextExpansionState.length) {
    nextExpansionState.splice(removedIndex, 1);
  }

  return nextExpansionState;
};
